import { GridColDef, GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid';
import ResultProgress from '@components/ResultProgress';
import {
    QuestionResponse,
    Result,
    calculateChildProgress,
} from '@synergetx/puppet-interview-types';
import { format } from 'date-fns/format';
import { useMemo } from 'react';

function RenderResponse({ response }: { response: QuestionResponse }) {
    if (typeof response === 'string') {
        return <>{response}</>;
    } else if (typeof response === 'object') {
        return (
            <>
                {Object.keys(response)
                    .filter((k) => response?.[k] === true)
                    .join(', ')}
            </>
        );
    } else {
        return <>{JSON.stringify(response)}</>;
    }
}

export function useResultsColumns(results: Result[]): GridColDef<Result>[] {
    return useMemo(() => {
        const baseColumns: GridColDef<Result>[] = [
            {
                field: 'id',
                headerName: 'Result ID',
                width: 280,
            },
            {
                field: 'createdAt',
                headerName: 'Created At',
                width: 180,
                valueFormatter: (value: number) => (value ? format(value, 'Pp') : ''),
            },
            {
                field: 'updatedAt',
                headerName: 'Updated At',
                width: 180,
                valueFormatter: (value: number) => (value ? format(value, 'Pp') : ''),
            },
            {
                field: 'interviewer',
                headerName: 'Assessor',
                width: 150,
            },
            {
                field: 'progress',
                headerName: 'Progress',
                width: 150,
                sortComparator: (v1, v2, param1, param2) =>
                    calculateChildProgress(param1.api.getRow(param1.id) as Result) -
                    calculateChildProgress(param2.api.getRow(param2.id) as Result),
                renderCell: ({ row }) => <ResultProgress result={row} />,
            },
        ];

        // Add dynamic answer columns based on first result
        const answerColumns: GridColDef<Result>[] =
            results[0]?.answers.map((a, i) => ({
                field: `answer_${i}`,
                headerName: a.name,
                flex: 1,
                minWidth: 150,
                valueGetter: (_value: unknown, row: Result) => {
                    const response = row.answers?.[i]?.response;
                    if (typeof response === 'string') return response;
                    if (typeof response === 'object') {
                        return Object.keys(response)
                            .filter((k) => response?.[k] === true)
                            .join(', ');
                    }
                    return JSON.stringify(response);
                },
                renderCell: ({ row }) => <RenderResponse response={row.answers?.[i]?.response} />,
                sortComparator: (v1, v2) => String(v1 ?? '').localeCompare(String(v2 ?? '')),
            })) ?? [];

        const metadataColumns: GridColDef<Result>[] = [
            {
                field: 'initialAppVersion',
                headerName: 'Initial App Version',
                width: 150,
            },
            {
                field: 'latestAppVersion',
                headerName: 'Latest App Version',
                width: 150,
            },
            {
                field: 'initialInterviewVersion',
                headerName: 'Initial Survey Version',
                width: 160,
            },
            {
                field: 'latestInterviewVersion',
                headerName: 'Latest Survey Version',
                width: 160,
            },
        ];

        return [...baseColumns, ...answerColumns, ...metadataColumns];
    }, [results]);
}

export function useResultsColumnVisibility(results: Result[]): GridColumnVisibilityModel {
    return useMemo(() => {
        const visibility: GridColumnVisibilityModel = {
            id: false,
            interviewer: false,
            initialAppVersion: false,
            latestAppVersion: false,
            initialInterviewVersion: false,
            latestInterviewVersion: false,
        };

        // Hide answer columns after the first 2
        results[0]?.answers.forEach((_, i) => {
            if (i >= 2) {
                visibility[`answer_${i}`] = false;
            }
        });

        return visibility;
    }, [results]);
}

export const RESULTS_INITIAL_SORT: GridSortModel = [{ field: 'updatedAt', sort: 'desc' }];
